import { useState, useEffect, useCallback } from "react";
import { PageTable, Header } from "/src/layouts/page";
import Table from "/src/components/table";
import "/src/components/UI/Button/IconButton/IconButton.css";
import ModalManager from "@ebay/nice-modal-react";
import knowledgeService from "../../services/KnowledgeService";
import brainService from "../../services/BrainService";
import NiceModal from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import KnowledgeModal from "../modal/KnowledgeModal";
import { formatDate } from "/src/utils/formatDate";

function KnowledgeTable({ brainId }) {
    const [knowledgeData, setKnowledgeData] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [brainName, setBrainName] = useState("");

    const fetchKnowledge = useCallback(
        async (params = {}) => {
            try {
                const response = await knowledgeService.getKnowledge(brainId, params);
                if (response.error) {
                    console.error("Error fetching knowledge:", response.error);
                    return [[], response.error];
                }
                setKnowledgeData(response[0].knowledges || []);
                return [response[0].knowledges, null];
            } catch (error) {
                console.error("Unexpected error fetching knowledge:", error);
                return [[], error];
            }
        },
        [brainId]
    );

    const fetchBrain = async () => {
        try {
            const [response, error] = await brainService.getBrain(brainId);
            if (error) return [null, error];
            setBrainName(response.name);
        } catch (error) {
            return [null, error];
        }
    };

    useEffect(() => {
        fetchKnowledge();
        fetchBrain();
    }, [brainId]);

    const cells = [
        {
            column: "NAME",
            name: "file_name",
            sort: true,
            show: (value) => value,
        },
        {
            column: "STATUS",
            name: "status",
            show: (value) =>
                value === "UPLOADED" ? (
                    <i className="fa fa-check" aria-hidden="true" style={{ color: "var(--green)" }} />
                ) : (
                    <span style={{ color: "var(--pink)", fontWeight: "bolder" }}>X</span>
                ),
        },
        {
            column: "LAST UPDATE",
            name: "updated_at",
            sort: true,
            show: (value) => formatDate(value),
        },
    ];

    const showAddModal = ({ addRow }) =>
        ModalManager.show(KnowledgeModal, {
            title: "Add Knowledge",
            sub_title: "Feed your brain with knowledges from documents or websites",
            brainId: brainId,
            onUploadComplete: () => {
                fetchKnowledge();
                setRefreshKey((prev) => prev + 1);
            },
        }).then((knowledge) => addRow(knowledge));

    const onClickDelete = async ({ row, deleteRow }) => {
        const { id, file_name } = row;
        await NiceModal.show(ApiModal, {
            title: "Delete resource ",
            body: "You really want to delete resource " + file_name + " ?",
            api: {
                request: (knowledgeId) => knowledgeService.deleteKnowledge(knowledgeId, brainId),
                param: [id],
            },
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Delete", type: "submit" },
            ],
        }).then(() => deleteRow(id));
    };

    const onClickDownload = async ({ row }) => {
        const { file_name, id } = row;

        if (/^https?:\/\//.test(file_name)) {
            window.open(file_name, "_blank");
            return;
        }
        try {
            const getUrlFile = await knowledgeService.downloadKnowledge(id);
            const url = getUrlFile[0].signedURL;

            if (!url) throw new Error("Download URL not found");

            const response = await fetch(url, { method: "GET" });
            if (!response.ok) throw new Error("Failed to fetch the file");

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = file_name || "downloaded_file";
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading the file:", error);
        }
    };

    const onClickOpenInNewTab = async ({ row }) => {
        const { file_name, id } = row;

        if (/^https?:\/\//.test(file_name)) {
            window.open(file_name, "_blank");
            return;
        }
        try {
            const getUrlFile = await knowledgeService.downloadKnowledge(id);
            const url = getUrlFile[0].signedURL;

            if (!url) throw new Error("Download URL not found");

            window.open(url, "_blank");
        } catch (error) {
            console.error("Error opening the URL in new tab:", error);
        }
    };

    const actions = [
        { className: "fas fa-up-right-from-square action-button edit_action", style: {}, onClick: onClickOpenInNewTab },
        { className: "fas fa-upload action-button edit_action", style: {}, onClick: onClickDownload },
        { className: "fas fa-trash action-button delete_action", style: {}, onClick: onClickDelete },
    ].filter((action) => action);

    const buttons = [{ icon: "fas fa-file-arrow-down", onClick: showAddModal }];

    const filters = [{ type: "input", placeholder: "resources", name: "file_name" }];

    return (
        <PageTable>
            <Header title={`Brain: ${brainName}`}></Header>
            <Table
                key={refreshKey}
                name="Brains List"
                buttons={buttons}
                actions={actions}
                cells={cells}
                api={fetchKnowledge}
                withPagination={false}
                filters={filters}
                defaultSort={{ column: "updated_at", val: false }}></Table>
        </PageTable>
    );
}

export default KnowledgeTable;
